import { chainCommands } from "prosemirror-commands";
import { liftListItem, sinkListItem } from "prosemirror-schema-list";
import { Command, EditorState } from "prosemirror-state";
import { useMemo } from "react";
import { setNodeAttributes } from "../commands/setNodeAttributes";
import { withRestoreDefaultFontMarksOnNewParagraph } from "../commands/withRestoreDefaultFontMarksOnNewParagraph";
import {
	doesSupportIndention,
	IndentionNodeAttributesPart,
	isIndentionNodeAttributes
} from "../schema/nodes/attributes/indention";
import { Schema } from "prosemirror-model";
import { hasNodes } from "../schema";

export const INDENTION_STEP_IN_PX = 30;

export type IndentionToolHandlers = {
	increaseIndentionHandler(): void;
	decreaseIndentionHandler(): void;
	hasIndention: boolean;
};

export function useIndentionTools(
	editorState: EditorState,
	applyCommand: (command: Command) => void,
	focus?: () => void
): IndentionToolHandlers {

	const hasIndention = useMemo(
		(): boolean => {
			const schema: Schema<never, never> = editorState.schema;

			if (hasNodes(schema, ["listItem"])) {
				const liftCommand: Command = liftListItem(schema.nodes.listItem);
				const hasListIndention: boolean = liftCommand(editorState);

				if (hasListIndention) {
					return true;
				}
			}

			const {
				selection: {
					from,
					to
				},
				doc
			} = editorState;

			let result: boolean = false;

			doc.nodesBetween(
				from,
				to,
				(node): false | undefined => {
					if (result) {
						return false;
					}

					if (!doesSupportIndention(node.type.spec)) {
						return undefined;
					}

					const currentIndention: number = getCurrentIndention(node.attrs);
					if (currentIndention > 0) {
						result = true;
					}

					return undefined;
				}
			);

			return result;
		},
		[editorState]
	);

	return useMemo(
		(): IndentionToolHandlers => {
			const schema: Schema<never, never> = editorState.schema;

			return {
				increaseIndentionHandler: (): void => {
					const commandsToChain: Command[] = [];

					if (hasNodes(schema, ["listItem"])) {
						const indentList: Command = sinkListItem(schema.nodes.listItem);
						commandsToChain.push(
							withRestoreDefaultFontMarksOnNewParagraph(indentList)
						);
					}

					commandsToChain.push(
						indentParagraph
					);

					applyCommand(
						chainCommands(...commandsToChain)
					);
					focus?.();
				},
				decreaseIndentionHandler: (): void => {
					const commandsToChain: Command[] = [
						decreaseForParagraph
					];

					if (hasNodes(schema, ["listItem"])) {
						const decreaseForList: Command = liftListItem(schema.nodes.listItem);
						commandsToChain.push(withRestoreDefaultFontMarksOnNewParagraph(decreaseForList));
					}

					applyCommand(
						chainCommands(...commandsToChain)
					);
					focus?.();
				},
				hasIndention
			};
		},
		[
			applyCommand,
			editorState.schema,
			focus,
			hasIndention
		]
	);
}

function getCurrentIndention(attrs: { [key: string]: string; }): number {
	if (isIndentionNodeAttributes(attrs)) {
		return attrs.indention;
	}

	return 0;
}

function discretize(value: number, divider: number): number {
	return Math.floor(value / divider) * divider;
}

const indentParagraph: Command = setNodeAttributes(
	attrs => {
		const indentionAttributes: IndentionNodeAttributesPart = {
			indention: discretize(getCurrentIndention(attrs), INDENTION_STEP_IN_PX) + INDENTION_STEP_IN_PX
		};

		return {
			...attrs,
			...indentionAttributes
		};
	},
	doesSupportIndention,
	() => false
);

const decreaseForParagraph: Command = setNodeAttributes(
	attrs => {
		const indentionAttributes: IndentionNodeAttributesPart = {
			indention: Math.max(
				0,
				discretize(getCurrentIndention(attrs), INDENTION_STEP_IN_PX) - INDENTION_STEP_IN_PX
			)
		};

		return {
			...attrs,
			...indentionAttributes
		};
	},
	doesSupportIndention,
	(attrs) => getCurrentIndention(attrs) === 0
);
